import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";

import SafeMakeForm from "../form/SafeMakeForm";
import Timer from "components/Timer";
import {
  addStateSafeMake,
  editStateSafeMake,
  rowEditSafeMake,
  safeMakeFetching,
  safeMakeFilterText,
  safeMakeReFetching,
  selectedRowsStateSafeMake,
} from "../atoms/atoms";
import useGetSafeMake from "../hooks/useGetSafeMake";
import useDeleteSafeMake from "../hooks/useDeleteSafeMake";
import { ISafeMake, ISafeMakeTableData } from "../types";
import TableFilters from "components/TableFilters";

export const SafeMakeTable: React.FC = () => {
  const { data: directData, refetch: reFetch } = useGetSafeMake();

  const BulkDelete = useDeleteSafeMake();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateSafeMake);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateSafeMake
  );
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateSafeMake);
  const [filterTextState, setFilterTextState] =
    useRecoilState(safeMakeFilterText);
  const setEditRowData = useSetRecoilState(rowEditSafeMake);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(safeMakeFetching);
  const isReFetching = useRecoilValue(safeMakeReFetching);

  //Local States
  const [tableData, setTableData] = useState<ISafeMake[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<ISafeMake[]>([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_securityconfiguration_safemake_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_securityconfiguration_safemake_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_securityconfiguration_safemake_edit;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  // Get Data
  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: ISafeMake[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].safeMake);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  //For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.safeMake &&
        item.safeMake
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: ISafeMakeTableData[]) => {
    const forDelete = rows
      .filter((x) => x.status)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: ISafeMakeTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.id);
    BulkDelete(ids);
    setSelectedRows([]);
    setRecent("");
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Safe Make/s" : "Safe Make"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Safe Make Name",
      field: "safeMake",
      customSort: (a, b) =>
        a.safeMake && b.safeMake
          ? a.safeMake.trim().localeCompare(b.safeMake.trim())
          : a.safeMake
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "status",
      render: (rowData) => {
        return rowData?.status ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <SafeMakeForm setRecent={setRecent} />
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              textInputName={"SafeMakeName"}
              columnDetails={"Safe Make Name"}
              filterText={setFilterTextState}
              recentText={recent}
              setRecent={setRecent}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div className="">
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Safe Make"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div className="">
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                  ? tableData
                  : filterTextState === ""
                  ? tableFilteredData
                  : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Safe Make"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};

export default SafeMakeTable;
